/**
 * Dev-sync J1 — Plotly trace assembly for the psychrometric chart.
 *
 * Line-by-line port of the trace/format half of legacy
 * `static/mvc/psychrometric_chart/view/psychrometric_chart.js` (2,423 lines,
 * readable via `git show origin/dev:…`). Cites below are line numbers in that
 * file. Everything here is a pure function of (data, season filter,
 * staleBySeason) — no DOM, no Plotly calls — so the popup can rebuild traces
 * on any season switch without a network round-trip (the season <select> is
 * client-side only; both seasons ship in every payload).
 */

import type {
  PsyAnnotation,
  PsyAxes,
  PsyChartData,
  PsyConditions,
  PsySeason,
  PsySeasonFilter,
  PsyStaleBySeason,
  PsyTunnelEntry,
  PsyTunnelPath,
} from './psychrometricApi';
import type { PsyScatterTrace } from './usePlotly';

// ---------------------------------------------------------------------------
// Constants (js:12-59)
// ---------------------------------------------------------------------------

export const COLORS = {
  saturation: '#1565c0',
  rhLine: 'rgba(120, 120, 120, 0.35)',
  enthalpyLine: 'rgba(180, 80, 0, 0.30)',
  wetBulbLine: 'rgba(20, 110, 160, 0.30)',
  summer: '#d62728',
  winter: '#1f77b4',
  missing: '#999',
  annotation: '#2ca02c',
  // Same red as the sidebar's stale text/re-rate button (#c62828) — close to
  // summer's own trace color but requested explicitly: staleness reads as
  // "red", full stop, matching the sidebar (js:22-26).
  stale: '#c62828',
} as const;

/** Kinds with an actual rating dialog a "needs RE-RATE" note can jump to —
 * mirrors the backend's RERATE_KINDS (js:29-35). */
export const RERATE_KINDS: Record<string, true> = {
  coil: true,
  fan: true,
  energy_wheel: true,
  plate_hex: true,
  filter: true,
  evap_pack: true,
  gas_heater: true,
};

/** Sidebar-button split (js:37-46): of RERATE_KINDS, only these get the
 * RERATE COMPONENT button (dialog jump); the rest fall through to
 * SYNCHRONIZE. */
export const DIALOG_RERATE_KINDS: Record<string, true> = {
  coil: true,
  energy_wheel: true,
  plate_hex: true,
  gas_heater: true,
};

/** SYNCHRONIZE button label suffix by component_kind (js:53-57); a genuine
 * plenum (component_kind null) is the "SECTION" case, handled directly. */
export const SYNC_LABELS: Record<string, string> = {
  fan: 'COMPONENT',
  silencer: 'COMPONENT',
  filter: 'COMPONENT',
  eliminator: 'COMPONENT',
  evap_pack: 'COMPONENT',
  electric_heater: 'ACCESSORY',
  humidifier: 'ACCESSORY',
};

export const UDA_KINDS: Record<string, true> = { electric_heater: true, humidifier: true };

export const DEFAULT_AXES: PsyAxes = { t_db_min: 20, t_db_max: 120, w_min: 0, w_max: 0.03 };

// ---------------------------------------------------------------------------
// Formatters (js:85-223)
// ---------------------------------------------------------------------------

export function escapeHtml(s: unknown): string {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function fmtT(t: number | null | undefined): string {
  return t === null || t === undefined ? '—' : `${t.toFixed(2)} °F`;
}

export function fmtRH(rh: number | null | undefined): string {
  return rh === null || rh === undefined ? '—' : `${(rh * 100).toFixed(0)} %`;
}

/** Humidity ratio DISPLAYS in grains per pound dry air (1 lb = 7000 gr) —
 * the backend computes and stores lb/lb (js:96-101). */
export const GRAINS_PER_LB = 7000;

export function toGrains(w: number): number;
export function toGrains(w: number | null | undefined): number | null | undefined;
export function toGrains(w: number | null | undefined): number | null | undefined {
  return w === null || w === undefined ? w : w * GRAINS_PER_LB;
}

export function fmtW(w: number | null | undefined): string {
  return w === null || w === undefined ? '—' : `${(w * GRAINS_PER_LB).toFixed(2)} gr/lb`;
}

export function fmtH(h: number | null | undefined): string {
  return h === null || h === undefined ? '—' : `${h.toFixed(2)} BTU/lb`;
}

/** Fixed-width label column (nbsp-padded) — relies on the hoverlabel's
 * per-trace monospace font so a character count maps to pixels (js:110-117). */
function padLabel(label: string, width: number): string {
  let pad = '';
  for (let i = label.length; i < width; i++) pad += '&nbsp;';
  return label + pad;
}

function fmtConditions(state: PsyConditions | null | undefined): string {
  if (!state) return '—';
  return (
    padLabel('DB', 5) +
    fmtT(state.t_db) +
    '<br>' +
    padLabel('WB', 5) +
    fmtT(state.t_wb) +
    '<br>' +
    padLabel('RH', 5) +
    fmtRH(state.rh) +
    '<br>' +
    padLabel('W', 5) +
    fmtW(state.w) +
    '<br>' +
    padLabel('h', 5) +
    fmtH(state.h)
  );
}

// Plotly hoverlabel auto-sizes to text bounds and exposes no padding hook, so
// padding lives inside the content (js:126-143).
const H_PAD = '&nbsp;'; // 2 nbsp ≈ 10 px
const V_PAD_LINE = '<span style="font-size:2px">&nbsp;</span>'; // ~6 px
const V_PAD_LINE_BOTTOM = '<span style="font-size:1px;">&nbsp;</span>'; // ~3 px

function padHover(text: string): string {
  const lines = text.split('<br>').map((ln) => H_PAD + ln + H_PAD);
  lines.unshift(V_PAD_LINE);
  lines.push(V_PAD_LINE_BOTTOM);
  return lines.join('<br>');
}

/** Friendly long label; deliberately no section tag appended (js:144-149). */
function componentHoverName(state: PsyTunnelEntry): string {
  return state.component_long || state.component_label || '';
}

/** Rendered width (monospace chars) of one of our own hover lines —
 * strips tags, counts &nbsp; as one char (js:153-156). */
function lineWidth(html: string): number {
  return html.replace(/&nbsp;/g, ' ').replace(/<[^>]*>/g, '').length;
}

function nbspRepeat(n: number): string {
  let s = '';
  for (let i = 0; i < n; i++) s += '&nbsp;';
  return s;
}

/** Marker tooltip: stream+season header row, one "Name : Role" line per
 * component genuinely AT this exact point, then the shared conditions once
 * (js:162-199). */
function formatPointHover(
  items: { name: string; role: string }[] | null,
  season: PsySeason,
  conditions: PsyConditions,
  streamLabel: string | null
): string {
  const seasonLabel = season.charAt(0).toUpperCase() + season.slice(1);
  const itemLines = (items || []).map((item) => `<b>${item.name}</b> : ${item.role}`);
  const conditionsHtml = fmtConditions(conditions);
  const bodyLines = itemLines.concat(conditionsHtml.split('<br>'));
  const maxWidth = bodyLines.reduce((max, ln) => Math.max(max, lineWidth(ln)), 0);

  let parts: string[] = [];
  if (streamLabel) {
    const pad = Math.max(1, maxWidth - streamLabel.length - seasonLabel.length);
    parts.push(`<b>${streamLabel}</b>${nbspRepeat(pad)}<b>${seasonLabel}</b>`);
  } else {
    parts.push(`<b>${seasonLabel}</b>`);
  }
  parts = parts.concat(itemLines);
  // 8px gap under the header lines, above the DB/WB/... table (js:194-196).
  parts.push('<span style="font-size:8px">&nbsp;</span>');
  parts.push(conditionsHtml);
  return padHover(parts.join('<br>'));
}

/** Hover for a chart spot shared by more than one section (js:200-207). */
function formatClusterHover(itemLabels: string[]): string {
  const lines = [`<b>${itemLabels.length} sections here:</b>`];
  itemLabels.forEach((label) => lines.push(`• ${label}`));
  return padHover(lines.join('<br>'));
}

function formatAnnotationHover(a: PsyAnnotation): string {
  const lines: string[] = [];
  if (a.label) {
    lines.push(`<b>${escapeHtml(a.label)}</b>`);
    // 8px spacer line stands in for a margin Plotly doesn't offer (js:212-215).
    lines.push('<span style="font-size:8px">&nbsp;</span>');
  }
  lines.push(padLabel('T_db', 7) + fmtT(a.t_db));
  lines.push(padLabel('T_wb', 7) + fmtT(a.t_wb));
  lines.push(padLabel('RH', 7) + fmtRH(a.rh));
  lines.push(padLabel('W', 7) + fmtW(a.w));
  lines.push(padLabel('h', 7) + fmtH(a.h));
  return padHover(lines.join('<br>'));
}

// ---------------------------------------------------------------------------
// Cluster / point-role / short-label passes (js:1693-1876)
// ---------------------------------------------------------------------------

/** Coarse fraction-of-axis-range bucket — points that round to distinct raw
 * values can still be pixel-on-pixel (js:1693-1708). */
function bucketKey(axes: PsyAxes, x: number | null | undefined, y: number | null | undefined): string | null {
  if (x === null || x === undefined || y === null || y === undefined) return null;
  const xRange = axes.t_db_max - axes.t_db_min || 1;
  const yRange = axes.w_max - axes.w_min || 1;
  const BUCKETS = 20;
  const xBucket = Math.round(((x - axes.t_db_min) / xRange) * BUCKETS);
  const yBucket = Math.round(((y - axes.w_min) / yRange) * BUCKETS);
  return `${xBucket},${yBucket}`;
}

/** Display name for one section within a shared-spot hover (js:1710-1720). */
function clusterLabel(state: PsyTunnelEntry, seasonKey: PsySeason): string {
  const long = state.component_long || state.component_short || '';
  const tag = state.component_label || '';
  const name = tag && tag !== long ? (long ? `${long} — ${tag}` : tag) : long || tag || 'Component';
  const seasonLabel = seasonKey ? seasonKey.charAt(0).toUpperCase() + seasonKey.slice(1) : '';
  return seasonLabel ? `${name} (${seasonLabel})` : name;
}

function seasonsFor(season: PsySeasonFilter): PsySeason[] {
  return season !== 'both' ? [season] : ['summer', 'winter'];
}

/** Every plottable "leaving" point (plus annotations), bucketed by chart spot
 * — a spot with >1 entry is "crowded" (js:1722-1752). Runs as its own pass
 * BEFORE any trace is built. */
function buildLabelClusters(
  data: PsyChartData,
  season: PsySeasonFilter,
  axes: PsyAxes
): Record<string, string[]> {
  const clusters: Record<string, string[]> = {};
  const record = (x: number | null | undefined, y: number | null | undefined, label: string) => {
    const key = bucketKey(axes, x, y);
    if (key === null) return;
    (clusters[key] || (clusters[key] = [])).push(label);
  };
  const seasons = seasonsFor(season);
  (data.tunnels || []).forEach((tunnel) => {
    seasons.forEach((seasonKey) => {
      ((tunnel.season_states || {})[seasonKey] || []).forEach((state) => {
        const leav = state.leaving;
        if (
          !leav ||
          leav.t_db === null ||
          leav.t_db === undefined ||
          leav.w === null ||
          leav.w === undefined
        )
          return;
        record(leav.t_db, leav.w, clusterLabel(state, seasonKey));
      });
    });
  });
  (data.annotations || []).forEach((a) => {
    record(a.t_db, a.w, a.label || 'Annotation');
  });
  return clusters;
}

/** Exact-coincidence key for point roles / short labels (js:1775-1777). */
function exactKey(season: PsySeason, x: number | null | undefined, y: number | null | undefined): string | null {
  if (x === null || x === undefined || y === null || y === undefined) return null;
  return `${season}:${x.toFixed(2)},${y.toFixed(6)}`;
}

/** Exact-coincidence roster for hover content — entering AND leaving points,
 * grouped by the exact (season, t_db, w) coordinate (js:1764-1801). */
function buildPointRoles(
  data: PsyChartData,
  season: PsySeasonFilter
): Record<string, { name: string; role: string }[]> {
  const roles: Record<string, { name: string; role: string }[]> = {};
  const record = (
    seasonKey: PsySeason,
    x: number | null | undefined,
    y: number | null | undefined,
    name: string,
    role: string
  ) => {
    const k = exactKey(seasonKey, x, y);
    if (k === null) return;
    (roles[k] || (roles[k] = [])).push({ name, role });
  };
  const seasons = seasonsFor(season);
  (data.tunnels || []).forEach((tunnel) => {
    seasons.forEach((seasonKey) => {
      ((tunnel.season_states || {})[seasonKey] || []).forEach((state) => {
        const ent = state.entering;
        const leav = state.leaving;
        const name = componentHoverName(state);
        if (ent && ent.t_db !== null && ent.t_db !== undefined && ent.w !== null && ent.w !== undefined) {
          record(seasonKey, ent.t_db, ent.w, name, 'Entering');
        }
        if (
          leav &&
          leav.t_db !== null &&
          leav.t_db !== undefined &&
          leav.w !== null &&
          leav.w !== undefined
        ) {
          record(seasonKey, leav.t_db, leav.w, name, 'Leaving');
        }
      });
    });
  });
  return roles;
}

/** Short on-chart text per plotted spot, exact-keyed; chains coincident
 * components in flow order ("HW ➜ CC"); bare plenums ("PL") dropped from any
 * multi-component chain (js:1808-1871). */
function buildShortLabels(data: PsyChartData, season: PsySeasonFilter): Record<string, string> {
  const byKey: Record<string, { short: string; order: number; sub: number }[]> = {};
  const record = (
    seasonKey: PsySeason,
    x: number | null | undefined,
    y: number | null | undefined,
    entry: { short: string; order: number; sub: number }
  ) => {
    const k = exactKey(seasonKey, x, y);
    if (k === null) return;
    (byKey[k] || (byKey[k] = [])).push(entry);
  };
  const seasons = seasonsFor(season);
  (data.tunnels || []).forEach((tunnel) => {
    seasons.forEach((seasonKey) => {
      ((tunnel.season_states || {})[seasonKey] || []).forEach((state) => {
        const ent = state.entering;
        const leav = state.leaving;
        const short = state.component_short || '';
        const order = state.order_index != null ? state.order_index : 0;
        if (ent && ent.t_db !== null && ent.t_db !== undefined && ent.w !== null && ent.w !== undefined) {
          record(seasonKey, ent.t_db, ent.w, { short, order, sub: 0 });
        }
        if (
          leav &&
          leav.t_db !== null &&
          leav.t_db !== undefined &&
          leav.w !== null &&
          leav.w !== undefined
        ) {
          record(seasonKey, leav.t_db, leav.w, { short, order, sub: 1 });
        }
      });
    });
  });
  const labels: Record<string, string> = {};
  Object.keys(byKey).forEach((k) => {
    const entries = byKey[k].slice().sort((a, b) => a.order - b.order || a.sub - b.sub);
    const parts: string[] = [];
    entries.forEach((e) => {
      if (!e.short) return;
      if (e.short === 'PL' && entries.length > 1) return;
      if (parts[parts.length - 1] !== e.short) parts.push(e.short);
    });
    if (!parts.length && entries.length) parts.push(entries[0].short || 'PL');
    labels[k] = parts.join(' ➜ ');
  });
  return labels;
}

// ---------------------------------------------------------------------------
// Trace assembly (js:1591-1691, js:1878-2148)
// ---------------------------------------------------------------------------

interface TraceContext {
  axes: PsyAxes;
  clusters: Record<string, string[]>;
  pointRoles: Record<string, { name: string; role: string }[]>;
  shortLabels: Record<string, string>;
  staleBySeason: PsyStaleBySeason;
}

function clusterAt(ctx: TraceContext, x: number | null | undefined, y: number | null | undefined): string[] | null {
  const key = bucketKey(ctx.axes, x, y);
  return key === null ? null : ctx.clusters[key] || null;
}

function isCrowded(ctx: TraceContext, x: number | null | undefined, y: number | null | undefined): boolean {
  const items = clusterAt(ctx, x, y);
  return !!items && items.length > 1;
}

function pointRolesAt(
  ctx: TraceContext,
  season: PsySeason,
  x: number | null | undefined,
  y: number | null | undefined
): { name: string; role: string }[] | null {
  const k = exactKey(season, x, y);
  return k === null ? null : ctx.pointRoles[k] || null;
}

function shortLabelAt(
  ctx: TraceContext,
  season: PsySeason,
  x: number | null | undefined,
  y: number | null | undefined
): string {
  const k = exactKey(season, x, y);
  return k === null ? '' : ctx.shortLabels[k] || '';
}

function hasCoords(pt: PsyConditions | null | undefined): pt is PsyConditions & { t_db: number; w: number } {
  return !!pt && pt.t_db !== null && pt.t_db !== undefined && pt.w !== null && pt.w !== undefined;
}

function pointsShare(a: PsyConditions | null | undefined, b: PsyConditions | null | undefined): boolean {
  return (
    hasCoords(a) &&
    hasCoords(b) &&
    Math.abs(a.t_db - b.t_db) < 0.05 &&
    Math.abs((a.w as number) - (b.w as number)) < 1e-6
  );
}

/** Per-path per-season traces (js:1878-2148). Line style encodes AIR STREAM
 * (solid Supply / dotted Return / dashed Exhaust / dash-dot No Air Stream),
 * split into separate traces because Plotly's line.dash is one value per
 * trace and a single path+season can mix streams. Trace push ORDER is
 * load-bearing — declutterLabels maps traces to `.scatterlayer .trace` DOM
 * nodes by index. */
function addTunnelTraces(
  ctx: TraceContext,
  traces: PsyScatterTrace[],
  tunnel: PsyTunnelPath,
  season: PsySeason,
  states: PsyTunnelEntry[]
): void {
  const color = season === 'winter' ? COLORS.winter : COLORS.summer;
  // Round for every point — season is already unambiguous from color (js:1880-1882).
  const markerSymbol = 'circle';
  const staleIds = ctx.staleBySeason[season] || {};
  const xs: number[] = [];
  const ys: number[] = [];
  const pointLabels: string[] = [];
  const hoverTexts: string[] = [];
  const symbols: string[] = [];
  const colors: string[] = [];
  const sizes: number[] = [];
  const textPositions: string[] = [];
  const solidLineXs: (number | null)[] = [];
  const solidLineYs: (number | null)[] = [];
  const dottedLineXs: (number | null)[] = [];
  const dottedLineYs: (number | null)[] = [];
  const dashedLineXs: (number | null)[] = [];
  const dashedLineYs: (number | null)[] = [];
  const dashdotLineXs: (number | null)[] = [];
  const dashdotLineYs: (number | null)[] = [];
  // "uncertain" (data error / not-rated entering) — dotted and faded
  // regardless of air stream (js:1897-1900).
  const uncertainLineXs: (number | null)[] = [];
  const uncertainLineYs: (number | null)[] = [];
  const staleSolidLineXs: (number | null)[] = [];
  const staleSolidLineYs: (number | null)[] = [];
  const staleDottedLineXs: (number | null)[] = [];
  const staleDottedLineYs: (number | null)[] = [];
  const staleDashedLineXs: (number | null)[] = [];
  const staleDashedLineYs: (number | null)[] = [];
  const staleDashdotLineXs: (number | null)[] = [];
  const staleDashdotLineYs: (number | null)[] = [];

  // Lines always connect entering→leaving even when the entering marker is
  // suppressed, so build them from the raw states (js:1914-1916).
  let prevLeav: PsyConditions | null = null;
  states.forEach((state, i) => {
    const ent = state.entering;
    const leav = state.leaving;
    const entClamped = !!(ent && ent.clamped);
    const entHasCoords = hasCoords(ent);
    const entOk = entHasCoords && !entClamped;
    const leavOk = hasCoords(leav);
    const shortLabel = state.component_short || '';
    const isStale = state.section_id != null && !!staleIds[state.section_id];
    const pointColor = isStale ? COLORS.stale : color;

    // Skip the entering marker when it coincides with the previous
    // component's leaving point — one component's leaving IS the next's
    // entering, and stacked markers break hover attribution (js:1928-1937).
    const sharesWithPrev = entHasCoords && pointsShare(prevLeav, ent);

    // Red alone says "diverged"; the "needs re-rate" note additionally
    // requires this point's own kind to be rateable (js:1939-1954).
    const needsRerate = isStale && !!RERATE_KINDS[state.component_kind ?? ''];
    const rerateNote = needsRerate ? ' needs re-rate' : '';
    const rerateHover = needsRerate ? '<br><i>Component needs re-rate</i>' : '';
    if (entHasCoords && !sharesWithPrev) {
      xs.push(ent.t_db);
      ys.push(toGrains(ent.w));
      // First point in the chain or a genuine break in it — same
      // chain-lookup-with-own-short-fallback as the leaving marker (js:1958-1967).
      const entChainLabel = shortLabelAt(ctx, season, ent.t_db, ent.w) || shortLabel;
      pointLabels.push(entChainLabel + rerateNote);
      // Below the dot so an entering/leaving pair don't stack labels on the
      // same side (js:1969-1973).
      textPositions.push('bottom center');
      hoverTexts.push(
        formatPointHover(pointRolesAt(ctx, season, ent.t_db, ent.w), season, ent, state.air_stream_label) +
          (entClamped
            ? '<br><i>(data error on entering)</i>'
            : state.rated
              ? ''
              : '<br><i>(not rated)</i>') +
          rerateHover
      );
      symbols.push(entOk ? markerSymbol : `${markerSymbol}-open`);
      colors.push(entOk ? pointColor : COLORS.missing);
      sizes.push(11);
    }
    if (leavOk) {
      const crowd = clusterAt(ctx, leav.t_db, leav.w);
      const crowded = !!crowd && crowd.length > 1;
      xs.push(leav.t_db);
      ys.push(toGrains(leav.w));
      // Every plotted spot gets a short label — a crowded spot shows the
      // exact-coincidence chain ("HW ➜ CC") instead of this state's own
      // short alone (js:1985-1992).
      const chainLabel = shortLabelAt(ctx, season, leav.t_db, leav.w) || shortLabel;
      pointLabels.push(chainLabel + rerateNote);
      // This leaving point doubles as the next entering — nudge its label
      // off the merged dot's centerline (js:1994-1999).
      const nextState = states[i + 1];
      const leavesIntoNext = !!nextState && pointsShare(leav, nextState.entering);
      textPositions.push(leavesIntoNext ? 'top right' : 'top center');
      hoverTexts.push(
        formatPointHover(pointRolesAt(ctx, season, leav.t_db, leav.w), season, leav, state.air_stream_label) +
          rerateHover
      );
      symbols.push(markerSymbol);
      colors.push(pointColor);
      sizes.push(crowded ? 14 : 11);
    }
    if (entHasCoords && leavOk) {
      // Supply → solid, Return → dotted, Exhaust → dashed, No Air Stream →
      // dash-dot; unknown falls back to dotted (js:2005-2013).
      const streamDash =
        state.air_stream_label === 'Supply'
          ? 'solid'
          : state.air_stream_label === 'Exhaust'
            ? 'dash'
            : state.air_stream_label === 'No Air Stream'
              ? 'dashdot'
              : 'dot';
      if (isStale) {
        if (streamDash === 'solid') {
          staleSolidLineXs.push(ent.t_db, leav.t_db, null);
          staleSolidLineYs.push(toGrains(ent.w), toGrains(leav.w), null);
        } else if (streamDash === 'dash') {
          staleDashedLineXs.push(ent.t_db, leav.t_db, null);
          staleDashedLineYs.push(toGrains(ent.w), toGrains(leav.w), null);
        } else if (streamDash === 'dashdot') {
          staleDashdotLineXs.push(ent.t_db, leav.t_db, null);
          staleDashdotLineYs.push(toGrains(ent.w), toGrains(leav.w), null);
        } else {
          staleDottedLineXs.push(ent.t_db, leav.t_db, null);
          staleDottedLineYs.push(toGrains(ent.w), toGrains(leav.w), null);
        }
      } else if (entOk) {
        if (streamDash === 'solid') {
          solidLineXs.push(ent.t_db, leav.t_db, null);
          solidLineYs.push(toGrains(ent.w), toGrains(leav.w), null);
        } else if (streamDash === 'dash') {
          dashedLineXs.push(ent.t_db, leav.t_db, null);
          dashedLineYs.push(toGrains(ent.w), toGrains(leav.w), null);
        } else if (streamDash === 'dashdot') {
          dashdotLineXs.push(ent.t_db, leav.t_db, null);
          dashdotLineYs.push(toGrains(ent.w), toGrains(leav.w), null);
        } else {
          dottedLineXs.push(ent.t_db, leav.t_db, null);
          dottedLineYs.push(toGrains(ent.w), toGrains(leav.w), null);
        }
      } else {
        uncertainLineXs.push(ent.t_db, leav.t_db, null);
        uncertainLineYs.push(toGrains(ent.w), toGrains(leav.w), null);
      }
    }
    prevLeav = leavOk ? leav : null;
  });
  if (solidLineXs.length) {
    traces.push({
      x: solidLineXs,
      y: solidLineYs,
      mode: 'lines',
      line: { color, width: 2, dash: 'solid' },
      hoverinfo: 'skip',
      name: `${tunnel.name} (${season})`,
    });
  }
  if (dottedLineXs.length) {
    traces.push({
      x: dottedLineXs,
      y: dottedLineYs,
      mode: 'lines',
      line: { color, width: 2, dash: 'dot' },
      hoverinfo: 'skip',
      name: `${tunnel.name} (${season})`,
    });
  }
  if (dashedLineXs.length) {
    traces.push({
      x: dashedLineXs,
      y: dashedLineYs,
      mode: 'lines',
      line: { color, width: 2, dash: 'dash' },
      hoverinfo: 'skip',
      name: `${tunnel.name} (${season})`,
    });
  }
  if (dashdotLineXs.length) {
    traces.push({
      x: dashdotLineXs,
      y: dashdotLineYs,
      mode: 'lines',
      line: { color, width: 2, dash: 'dashdot' },
      hoverinfo: 'skip',
      name: `${tunnel.name} (${season})`,
    });
  }
  if (uncertainLineXs.length) {
    traces.push({
      x: uncertainLineXs,
      y: uncertainLineYs,
      mode: 'lines',
      line: { color, width: 1, dash: 'dot' },
      opacity: 0.5,
      hoverinfo: 'skip',
      name: `${tunnel.name} (${season}, uncertain)`,
    });
  }
  if (staleSolidLineXs.length) {
    traces.push({
      x: staleSolidLineXs,
      y: staleSolidLineYs,
      mode: 'lines',
      line: { color: COLORS.stale, width: 2, dash: 'solid' },
      hoverinfo: 'skip',
      name: `${tunnel.name} (${season}, needs re-rate)`,
    });
  }
  if (staleDottedLineXs.length) {
    traces.push({
      x: staleDottedLineXs,
      y: staleDottedLineYs,
      mode: 'lines',
      line: { color: COLORS.stale, width: 2, dash: 'dot' },
      hoverinfo: 'skip',
      name: `${tunnel.name} (${season}, needs re-rate)`,
    });
  }
  if (staleDashedLineXs.length) {
    traces.push({
      x: staleDashedLineXs,
      y: staleDashedLineYs,
      mode: 'lines',
      line: { color: COLORS.stale, width: 2, dash: 'dash' },
      hoverinfo: 'skip',
      name: `${tunnel.name} (${season}, needs re-rate)`,
    });
  }
  if (staleDashdotLineXs.length) {
    traces.push({
      x: staleDashdotLineXs,
      y: staleDashdotLineYs,
      mode: 'lines',
      line: { color: COLORS.stale, width: 2, dash: 'dashdot' },
      hoverinfo: 'skip',
      name: `${tunnel.name} (${season}, needs re-rate)`,
    });
  }
  if (xs.length) {
    traces.push({
      x: xs,
      y: ys,
      mode: 'markers+text',
      marker: { color: colors, symbol: symbols, size: sizes, line: { width: 1, color: '#222' } },
      text: pointLabels,
      textposition: textPositions,
      textfont: { size: 10, color: '#222' },
      hovertext: hoverTexts,
      hovertemplate: '%{hovertext}<extra></extra>',
      // Monospace so fmtConditions's fixed nbsp-count columns line up
      // pixel-exact (js:2141-2144).
      hoverlabel: {
        align: 'left',
        bgcolor: '#fff',
        bordercolor: color,
        font: { size: 12, family: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace" },
      },
      name: `${tunnel.name} (${season}) states`,
    });
  }
}

/**
 * The full trace list for one render pass (js:1591-1691): curve families
 * first (saturation, RH, enthalpy, wet-bulb), then per-path/per-season state
 * traces, then the annotations trace LAST.
 */
export function buildTraces(
  data: PsyChartData,
  season: PsySeasonFilter,
  staleBySeason: PsyStaleBySeason
): PsyScatterTrace[] {
  const traces: PsyScatterTrace[] = [];
  const curves = data.curves || ({} as PsyChartData['curves']);
  const axes = data.axes || DEFAULT_AXES;
  const ctx: TraceContext = {
    axes,
    clusters: buildLabelClusters(data, season, axes),
    pointRoles: buildPointRoles(data, season),
    shortLabels: buildShortLabels(data, season),
    staleBySeason,
  };

  if (curves.saturation && curves.saturation.length) {
    traces.push({
      x: curves.saturation.map((p) => p.t_db),
      y: curves.saturation.map((p) => toGrains(p.w)),
      mode: 'lines',
      line: { color: COLORS.saturation, width: 2 },
      hoverinfo: 'skip',
      name: 'Saturation',
    });
  }

  (curves.rh_lines || []).forEach((line) => {
    traces.push({
      x: line.points.map((p) => p.t_db),
      y: line.points.map((p) => toGrains(p.w)),
      mode: 'lines',
      line: { color: COLORS.rhLine, width: 1 },
      hoverinfo: 'skip',
      name: `${Math.round(line.rh * 100)}% RH`,
    });
  });

  (curves.enthalpy_lines || []).forEach((line) => {
    traces.push({
      x: line.points.map((p) => p.t_db),
      y: line.points.map((p) => toGrains(p.w)),
      mode: 'lines',
      line: { color: COLORS.enthalpyLine, width: 1, dash: 'dot' },
      hoverinfo: 'skip',
      name: `${line.h} BTU/lb`,
    });
  });

  (curves.wet_bulb_lines || []).forEach((line) => {
    traces.push({
      x: line.points.map((p) => p.t_db),
      y: line.points.map((p) => toGrains(p.w)),
      mode: 'lines',
      line: { color: COLORS.wetBulbLine, width: 1, dash: 'dashdot' },
      hoverinfo: 'skip',
      name: `${line.t_wb} °F WB`,
    });
  });

  (data.tunnels || []).forEach((tunnel) => {
    seasonsFor(season).forEach((seasonKey) => {
      const states = (tunnel.season_states || {})[seasonKey] || [];
      addTunnelTraces(ctx, traces, tunnel, seasonKey, states);
    });
  });

  const annotations = data.annotations || [];
  if (annotations.length) {
    traces.push({
      x: annotations.map((a) => a.t_db),
      y: annotations.map((a) => toGrains(a.w)),
      mode: 'markers+text',
      marker: { color: COLORS.annotation, size: 11, symbol: 'circle', line: { width: 1, color: '#1e7e1e' } },
      text: annotations.map((a) => {
        if (isCrowded(ctx, a.t_db, a.w)) return '';
        const label = a.label || '';
        return label ? `${label}` : '';
      }),
      // "top right": label starts at the dot's top and extends rightward
      // (js:1673-1676).
      textposition: 'top right',
      hovertext: annotations.map((a) => {
        const items = clusterAt(ctx, a.t_db, a.w);
        return items && items.length > 1 ? formatClusterHover(items) : formatAnnotationHover(a);
      }),
      hovertemplate: '%{hovertext}<extra></extra>',
      hoverlabel: {
        align: 'left',
        bgcolor: '#fff',
        bordercolor: COLORS.annotation,
        font: {
          size: 12,
          color: COLORS.annotation,
          family: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
        },
      },
      name: 'Annotations',
    });
  }
  return traces;
}

/** True only when a gray/open marker is actually drawn — a clamped ENTERING
 * point; checks every season key regardless of the current filter, exactly
 * like legacy (js:1070-1092). Used by the legend's "Data not rated" row. */
export function hasMissing(data: PsyChartData | null): boolean {
  const paths = (data && data.tunnels) || [];
  for (let i = 0; i < paths.length; i++) {
    const seasons = paths[i].season_states || {};
    const keys = Object.keys(seasons) as PsySeason[];
    for (let j = 0; j < keys.length; j++) {
      const arr = seasons[keys[j]] || [];
      for (let k = 0; k < arr.length; k++) {
        const ent = arr[k].entering;
        if (ent && ent.clamped) return true;
      }
    }
  }
  return false;
}
